import { AssistantRuntimeProvider } from '@assistant-ui/react';
import { AssistantChatTransport, useChatRuntime } from '@assistant-ui/react-ai-sdk';
import { useAuth, useOrganization, useSession, useUser } from '@clerk/clerk-react';
import { lastAssistantMessageIsCompleteWithToolCalls } from 'ai';
import { useEffect, useMemo, useState } from 'react';
import { usePostHog } from 'posthog-js/react';
import { getAuthToken, isAuthReady } from '../../utils/auth';

// Fast tick. Every iteration awaits `getAuthToken()` from `utils/auth.js`,
// which is the same helper TRPC uses, so the assistant transport stays in
// lockstep with the token TRPC ships on every request. Clerk JWTs are ~60s
// TTL; a 5s tick keeps worst-case staleness inside the authorizer's tolerance.
const ASSISTANT_TOKEN_TICK_MS = 5 * 1000;
const ACTIVE_THREAD_ID_GLOBAL = '__CHARITYSTACK_DASHBOARD_ASSISTANT_THREAD_ID__';

const DASHBOARD_ASSISTANT_ENABLED_VALUE =
  import.meta.env.VITE_APP_DASHBOARD_ASSISTANT_ENABLED ||
  import.meta.env.VITE_DASHBOARD_ASSISTANT_ENABLED;

// Single flag: UI and runtime always ship together. When true (or unset), the
// chat dock renders and connects to the assistant-ui runtime URL. When 'false',
// the entire assistant UI is hidden.
export const DASHBOARD_ASSISTANT_ENABLED =
  DASHBOARD_ASSISTANT_ENABLED_VALUE !== 'false';

const EXPLICIT_ASSISTANT_UI_RUNTIME_URL =
  import.meta.env.VITE_APP_ASSISTANT_UI_RUNTIME_URL ||
  import.meta.env.VITE_ASSISTANT_UI_RUNTIME_URL ||
  import.meta.env.VITE_APP_DASHBOARD_ASSISTANT_RUNTIME_URL ||
  import.meta.env.VITE_DASHBOARD_ASSISTANT_RUNTIME_URL;

const LEGACY_COPILOT_RUNTIME_URL =
  import.meta.env.VITE_APP_COPILOTKIT_RUNTIME_URL ||
  import.meta.env.VITE_COPILOTKIT_RUNTIME_URL;

function normalizeAssistantUiRuntimeUrl(runtimeUrl) {
  if (!runtimeUrl) {
    return '/api/assistant-ui';
  }

  return runtimeUrl.replace(/\/api\/copilotkit(?=$|[/?#])/, '/api/assistant-ui');
}

export const DASHBOARD_ASSISTANT_RUNTIME_URL =
  normalizeAssistantUiRuntimeUrl(EXPLICIT_ASSISTANT_UI_RUNTIME_URL || LEGACY_COPILOT_RUNTIME_URL);

function getPostHogTransportContext(posthog) {
  return {
    distinctId: posthog?.get_distinct_id?.(),
    sessionId: posthog?.get_session_id?.(),
  };
}

function DashboardAssistantRuntimeProvider({ children }) {
  const { isLoaded: isAuthLoaded, isSignedIn } = useAuth();
  const { organization } = useOrganization();
  const { session } = useSession();
  const { user } = useUser();
  const posthog = usePostHog();
  const [authToken, setAuthToken] = useState(null);

  const activeOrganizationId = useMemo(() => {
    const memberships = user?.organizationMemberships || [];
    return (
      organization?.id ||
      session?.lastActiveOrganizationId ||
      (memberships.length === 1 ? memberships[0]?.organization?.id : undefined)
    );
  }, [organization?.id, session?.lastActiveOrganizationId, user?.organizationMemberships]);

  useEffect(() => {
    if (!DASHBOARD_ASSISTANT_ENABLED || !isAuthLoaded || !isSignedIn) {
      setAuthToken(null);
      return undefined;
    }

    let cancelled = false;

    const refresh = async () => {
      if (cancelled || !isAuthReady()) return;
      try {
        // getAuthToken lazily refreshes when the cached JWT is within 30s of
        // expiry. On refresh failure it still returns the previously cached
        // token (better than dropping the Authorization header).
        const token = await getAuthToken();
        if (!cancelled) {
          setAuthToken(token || null);
        }
      } catch (error) {
        // Refresh genuinely failed and there's no cached token to fall back on.
        // Leave the existing state in place rather than blanking it — a stale
        // token will fail the authorizer with a clear "expired" message, which
        // is more debuggable than silently dropping auth.
        console.warn('[DashboardAssistant] Token refresh failed:', error);
      }
    };

    void refresh();
    const intervalId = window.setInterval(refresh, ASSISTANT_TOKEN_TICK_MS);

    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') void refresh();
    };
    document.addEventListener('visibilitychange', onVisibilityChange);

    return () => {
      cancelled = true;
      window.clearInterval(intervalId);
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, [isAuthLoaded, isSignedIn]);

  const transport = useMemo(
    () =>
      new AssistantChatTransport({
        api: DASHBOARD_ASSISTANT_RUNTIME_URL,
        credentials: 'include',
        body: () => {
          const posthogContext = getPostHogTransportContext(posthog);
          return {
            assistantRuntime: 'assistant-ui',
            threadId:
              typeof window !== 'undefined'
                ? window[ACTIVE_THREAD_ID_GLOBAL]
                : undefined,
            forwardedProps: {
              posthogDistinctId: posthogContext.distinctId,
              posthogSessionId: posthogContext.sessionId,
            },
          };
        },
        headers: () => {
          const posthogContext = getPostHogTransportContext(posthog);
          return {
            ...(authToken ? { Authorization: authToken } : {}),
            ...(activeOrganizationId
              ? { 'x-charitystack-organization-id': activeOrganizationId }
              : {}),
            ...(posthogContext.distinctId
              ? { 'x-posthog-distinct-id': posthogContext.distinctId }
              : {}),
            ...(posthogContext.sessionId
              ? { 'x-posthog-session-id': posthogContext.sessionId }
              : {}),
          };
        },
      }),
    [activeOrganizationId, authToken, posthog],
  );
  const runtime = useChatRuntime({
    sendAutomaticallyWhen: lastAssistantMessageIsCompleteWithToolCalls,
    transport,
  });

  return (
    <AssistantRuntimeProvider
      key={activeOrganizationId || (isAuthLoaded && !isSignedIn ? 'signed-out' : 'pending')}
      runtime={runtime}
    >
      {children}
    </AssistantRuntimeProvider>
  );
}

function DashboardAssistantProvider({ children }) {
  if (!DASHBOARD_ASSISTANT_ENABLED) {
    return children;
  }

  return <DashboardAssistantRuntimeProvider>{children}</DashboardAssistantRuntimeProvider>;
}

export default DashboardAssistantProvider;
